import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useState } from "react";
import { toast } from "sonner";
import { useReactivateTerminalActivationCodeMutation } from "Api/Mutations/Merchants/useReactivateTerminalActivationCodeMutation";
import { useSetComgateTerminalTidTypesMutation } from "Api/Mutations/Merchants/useSetComgateTerminalTidTypesMutation";
import { useTriggerTmsRegistrationMutation } from "Api/Mutations/Merchants/useTriggerTmsRegistrationMutation";
import {
  type ApiProblemDetails,
  type ComgateTerminalTidDto,
  TerminalType,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Pages.Content.Merchants.OrbitLOnboarding.Detail;

// The backend reports the exceeded contract counts under the Tids key.
const getSaveTypesErrorResource = (error: unknown) => {
  const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
  if (apiErrors && "Tids" in apiErrors) {
    return RESOURCES.SaveTidTypesExceedError;
  }
  return RESOURCES.SaveTidTypesError;
};

type Props = {
  merchantPublicID: string;
  tids: Array<ComgateTerminalTidDto>;
  isEditingEnabled: boolean;
};

// Comgate does not say whether a TID is a physical terminal or SoftPOS. When the contract has both,
// staff decides here; TMS registration skips TIDs without a type and takes the rest.
export const TidTypesSection = ({
  merchantPublicID,
  tids,
  isEditingEnabled,
}: Props) => {
  const { t } = useResource();
  const [pendingTypes, setPendingTypes] = useState<
    Record<string, TerminalType>
  >({});

  const { mutate: saveTypes, isPending: isSaving } =
    useSetComgateTerminalTidTypesMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.SaveTidTypesSuccess));
        setPendingTypes({});
      },
      onError: (error: unknown) =>
        toast.error(t(getSaveTypesErrorResource(error))),
    });

  const { mutate: registerInTms, isPending: isRegistering } =
    useTriggerTmsRegistrationMutation({
      onSuccess: (outcome) =>
        toast.success(
          t(RESOURCES.RegisterTmsSuccess, {
            registered: outcome.registeredCount,
            expected: outcome.expectedCount,
          }),
        ),
      onError: () => toast.error(t(RESOURCES.RegisterTmsError)),
    });

  // Only terminals that pair by activation code (SoftPOS app, P300) carry one; reactivation frees the code for another device.
  const { mutate: reactivateCode, isPending: isReactivating } =
    useReactivateTerminalActivationCodeMutation({
      onSuccess: () => toast.success(t(RESOURCES.ReactivateCodeSuccess)),
      onError: () => toast.error(t(RESOURCES.ReactivateCodeError)),
    });

  const unknownTids = tids.filter((tid) => tid.terminalType == null);
  const pendingEntries = Object.entries(pendingTypes);
  const canSave = pendingEntries.length > 0 && !isSaving;

  const typeLabels: Record<TerminalType, string> = {
    [TerminalType.Physical]: t(RESOURCES.TidTypePhysical),
    [TerminalType.SoftPos]: t(RESOURCES.TidTypeSoftPos),
  };
  const typeLabel = (type: TerminalType | null | undefined) =>
    type ? typeLabels[type] : t(RESOURCES.TidTypeUnknown);

  const handleTypeChange = (tid: string, value: TerminalType | "") => {
    if (value === "") {
      return;
    }
    setPendingTypes((prev) => ({ ...prev, [tid]: value }));
  };

  return (
    <Stack gap={1}>
      <Typography variant="subtitle2">
        {t(RESOURCES.TidTypesSection)}
      </Typography>
      {unknownTids.length > 0 && (
        <Alert severity="warning">{t(RESOURCES.TidTypesHint)}</Alert>
      )}
      <Stack component="ul" gap={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
        {tids.map((tid) => {
          const isUnknown = tid.terminalType == null;
          return (
            <Stack
              key={tid.tid}
              component="li"
              direction="row"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <Typography variant="body2" sx={{ minWidth: 120 }}>
                {t(RESOURCES.Tid)}: {tid.tid}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {tid.facilityName ?? tid.storeName ?? "—"}
              </Typography>
              {isUnknown && isEditingEnabled && !tid.isRegistered ? (
                <Select<TerminalType | "">
                  size="small"
                  displayEmpty
                  value={pendingTypes[tid.tid] ?? ""}
                  onChange={(e) => handleTypeChange(tid.tid, e.target.value)}
                  sx={{ minWidth: 160 }}
                >
                  <MenuItem value="" disabled>
                    {t(RESOURCES.TidTypeUnknown)}
                  </MenuItem>
                  <MenuItem value={TerminalType.Physical}>
                    {t(RESOURCES.TidTypePhysical)}
                  </MenuItem>
                  <MenuItem value={TerminalType.SoftPos}>
                    {t(RESOURCES.TidTypeSoftPos)}
                  </MenuItem>
                </Select>
              ) : (
                <Chip
                  label={typeLabel(tid.terminalType)}
                  size="small"
                  color={isUnknown ? "warning" : "default"}
                  variant="outlined"
                />
              )}
              {tid.isTypeManual && (
                <Chip
                  label={t(RESOURCES.TidTypeManual)}
                  size="small"
                  variant="outlined"
                />
              )}
              {tid.isRegistered && (
                <Chip
                  label={t(RESOURCES.TerminalStatusDeployed)}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
              {tid.activationCode && (
                <Typography variant="body2">
                  {t(RESOURCES.ActivationCode)}:{" "}
                  <Box component="code" sx={{ letterSpacing: 1 }}>
                    {tid.activationCode}
                  </Box>
                </Typography>
              )}
              {isEditingEnabled && tid.isRegistered && tid.activationCode && (
                <Button
                  size="small"
                  variant="text"
                  disabled={isReactivating}
                  onClick={() =>
                    reactivateCode({ merchantPublicID, tid: tid.tid })
                  }
                >
                  {t(RESOURCES.ReactivateCodeButton)}
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>
      {isEditingEnabled && (
        <Box display="flex" gap={1}>
          {canSave && (
            <Button
              variant="contained"
              size="small"
              disabled={isSaving}
              onClick={() =>
                saveTypes({
                  merchantPublicID,
                  tids: pendingEntries.map(([tid, terminalType]) => ({
                    tid,
                    terminalType,
                  })),
                })
              }
            >
              {t(RESOURCES.SaveTidTypes)}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<PlayArrowIcon />}
            disabled={isRegistering}
            onClick={() => registerInTms({ merchantPublicID })}
          >
            {t(RESOURCES.RegisterTmsButton)}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
